import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/services/supabase';
import type { RealtimeChannel, RealtimePresenceState } from '@supabase/supabase-js';

interface RealtimeConfig {
  table: string;
  schema?: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
}

export function useRealtimeSubscription<T>(
  config: RealtimeConfig,
  callback: (payload: { new: T; old: T; eventType: string }) => void
) {
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  useEffect(() => {
    const { table, schema = 'public', event = '*', filter } = config;

    const channelName = `realtime:${schema}:${table}:${filter || 'all'}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newChannel = (supabase.channel(channelName) as any)
      .on(
        'postgres_changes',
        {
          event,
          schema,
          table,
          filter,
        },
        (payload: { new: unknown; old: unknown; eventType: string }) => {
          callback({
            new: payload.new as T,
            old: payload.old as T,
            eventType: payload.eventType,
          });
        }
      )
      .subscribe();

    setChannel(newChannel);

    return () => {
      if (newChannel) {
        supabase.removeChannel(newChannel);
      }
    };
  }, [config.table, config.schema, config.event, config.filter, callback]);

  return channel;
}

// Interface for moderation counts
interface ModerationCounts {
  conversationFlags: number;
  reviewFlags: number;
  businessModeration: number;
  total: number;
}

// Hook for real-time moderation queue counts with badges
export function useModerationCounts() {
  const [counts, setCounts] = useState<ModerationCounts>({
    conversationFlags: 0,
    reviewFlags: 0,
    businessModeration: 0,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const channelsRef = useRef<RealtimeChannel[]>([]);

  const fetchCounts = useCallback(async () => {
    try {
      const [convResult, reviewResult, bizResult] = await Promise.all([
        supabase
          .from('conversation_flags')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('review_flags')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('business_moderation_queue')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
      ]);

      const conversationFlags = convResult.count || 0;
      const reviewFlags = reviewResult.count || 0;
      const businessModeration = bizResult.count || 0;

      setCounts({
        conversationFlags,
        reviewFlags,
        businessModeration,
        total: conversationFlags + reviewFlags + businessModeration,
      });
    } catch (error) {
      console.error('Error fetching moderation counts:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();

    // Subscribe to all moderation tables
    const convChannel = supabase
      .channel('web_admin_conv_flags')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversation_flags' },
        () => fetchCounts()
      )
      .subscribe();
    channelsRef.current.push(convChannel);

    const reviewChannel = supabase
      .channel('web_admin_review_flags')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'review_flags' },
        () => fetchCounts()
      )
      .subscribe();
    channelsRef.current.push(reviewChannel);

    const bizChannel = supabase
      .channel('web_admin_biz_mod')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'business_moderation_queue' },
        () => fetchCounts()
      )
      .subscribe();
    channelsRef.current.push(bizChannel);

    return () => {
      channelsRef.current.forEach(channel => supabase.removeChannel(channel));
      channelsRef.current = [];
    };
  }, [fetchCounts]);

  return { counts, isLoading, refresh: fetchCounts };
}

// Interface for admin presence
interface AdminPresence {
  userId: string;
  userName: string;
  onlineAt: string;
  currentPage?: string;
}

// Hook for admin presence tracking
export function useAdminPresence(currentUserId?: string, currentUserName?: string) {
  const [onlineAdmins, setOnlineAdmins] = useState<AdminPresence[]>([]);
  const [currentPage, setCurrentPage] = useState<string>('');
  const channelRef = useRef<RealtimeChannel | null>(null);

  const updateCurrentPage = useCallback((page: string) => {
    setCurrentPage(page);
    if (channelRef.current && currentUserId) {
      channelRef.current.track({
        user_id: currentUserId,
        user_name: currentUserName || 'Admin',
        online_at: new Date().toISOString(),
        current_page: page,
      });
    }
  }, [currentUserId, currentUserName]);

  useEffect(() => {
    if (!currentUserId) return;

    const channel = supabase.channel('web_admin_presence', {
      config: {
        presence: {
          key: currentUserId,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState() as RealtimePresenceState<{
          user_id: string;
          user_name: string;
          online_at: string;
          current_page?: string;
        }>;

        const admins: AdminPresence[] = [];

        for (const [userId, presences] of Object.entries(state)) {
          if (presences.length > 0) {
            const presence = presences[0];
            admins.push({
              userId,
              userName: presence.user_name,
              onlineAt: presence.online_at,
              currentPage: presence.current_page,
            });
          }
        }

        setOnlineAdmins(admins);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: currentUserId,
            user_name: currentUserName || 'Admin',
            online_at: new Date().toISOString(),
            current_page: currentPage,
          });
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        channelRef.current.untrack();
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [currentUserId, currentUserName, currentPage]);

  return { onlineAdmins, updateCurrentPage };
}

// Interface for notification
interface AdminNotification {
  id: string;
  type: 'conversation_flag' | 'review_flag' | 'payment_failure' | 'new_user' | 'new_business';
  title: string;
  message: string;
  data?: unknown;
  timestamp: string;
  read: boolean;
}

// Hook for real-time admin notifications
export function useAdminNotifications() {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const channelsRef = useRef<RealtimeChannel[]>([]);

  const addNotification = useCallback((notification: Omit<AdminNotification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: AdminNotification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  useEffect(() => {
    // Subscribe to conversation flags
    const convChannel = supabase
      .channel('notifications_conv_flags')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'conversation_flags' },
        (payload) => {
          addNotification({
            type: 'conversation_flag',
            title: 'New Conversation Flag',
            message: `A new ToS violation report requires review`,
            data: payload.new,
          });
        }
      )
      .subscribe();
    channelsRef.current.push(convChannel);

    // Subscribe to review flags
    const reviewChannel = supabase
      .channel('notifications_review_flags')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'review_flags' },
        (payload) => {
          addNotification({
            type: 'review_flag',
            title: 'New Review Flag',
            message: `A business reported a potential fake review`,
            data: payload.new,
          });
        }
      )
      .subscribe();
    channelsRef.current.push(reviewChannel);

    // Subscribe to payment failures
    const paymentChannel = supabase
      .channel('notifications_payments')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payment_records' },
        (payload) => {
          const record = payload.new as { status?: string };
          if (record?.status === 'failed') {
            addNotification({
              type: 'payment_failure',
              title: 'Payment Failed',
              message: `A payment has failed and may require attention`,
              data: payload.new,
            });
          }
        }
      )
      .subscribe();
    channelsRef.current.push(paymentChannel);

    // Subscribe to new users
    const userChannel = supabase
      .channel('notifications_users')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'user_profiles' },
        (payload) => {
          const user = payload.new as { full_name?: string; email?: string };
          addNotification({
            type: 'new_user',
            title: 'New User Registered',
            message: `${user?.full_name || user?.email || 'A new user'} joined the platform`,
            data: payload.new,
          });
        }
      )
      .subscribe();
    channelsRef.current.push(userChannel);

    // Subscribe to new businesses
    const bizChannel = supabase
      .channel('notifications_businesses')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'business_profiles' },
        (payload) => {
          const biz = payload.new as { business_name?: string };
          addNotification({
            type: 'new_business',
            title: 'New Business Registered',
            message: `${biz?.business_name || 'A new business'} joined the platform`,
            data: payload.new,
          });
        }
      )
      .subscribe();
    channelsRef.current.push(bizChannel);

    return () => {
      channelsRef.current.forEach(channel => supabase.removeChannel(channel));
      channelsRef.current = [];
    };
  }, [addNotification]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
  };
}

// Hook for monitoring moderation queue updates
export function useModerationRealtimeUpdates(
  onConversationFlag?: (data: unknown) => void,
  onReviewFlag?: (data: unknown) => void,
  onBusinessModeration?: (data: unknown) => void
) {
  useEffect(() => {
    const channels: RealtimeChannel[] = [];

    if (onConversationFlag) {
      const convChannel = supabase
        .channel('conversation_flags_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'conversation_flags' },
          (payload) => onConversationFlag(payload)
        )
        .subscribe();
      channels.push(convChannel);
    }

    if (onReviewFlag) {
      const reviewChannel = supabase
        .channel('review_flags_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'review_flags' },
          (payload) => onReviewFlag(payload)
        )
        .subscribe();
      channels.push(reviewChannel);
    }

    if (onBusinessModeration) {
      const bizChannel = supabase
        .channel('business_moderation_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'business_moderation_queue' },
          (payload) => onBusinessModeration(payload)
        )
        .subscribe();
      channels.push(bizChannel);
    }

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [onConversationFlag, onReviewFlag, onBusinessModeration]);
}

// Hook for monitoring new audit log entries
export function useAuditLogRealtime(onNewLog: (log: unknown) => void) {
  useEffect(() => {
    const channel = supabase
      .channel('audit_logs_changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'admin_audit_logs' },
        (payload) => onNewLog(payload.new)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [onNewLog]);
}
